<script setup lang="ts">
import { useAppStore } from '~/store/useAppStore'
import { useConfigStore } from '~/store/useConfigStore'
import type { Team } from '~/types'

interface Props {
  team?: Team.Model
}

const props = defineProps<Props>()
const { slug } = useTeam(props.team)
const router = useRouter()

const selectedTeam = computed<Maybe<string>>({
  get: () => slug.value,
  set: async (slug: Maybe<string>) => {
    await navigateTo(({ name: 'teams-slug', params: { slug } }))
  },
})

const reloadIndexPage = (): void => {
  if (router.currentRoute.value.name !== 'index') {
    return
  }

  router.go(0)
}

const configStore = useConfigStore()
const appStore = useAppStore()

const { headerMenu, websiteMainImage, slotContentBlocks, hasSlotContentBlocks } = storeToRefs(configStore)
const {
  isMainMenuOpened,
  isTickerOpened,
  isSearchOpened,
} = storeToRefs(appStore)
const {
  mainMenuClickHandler,
  searchClickHandler,
  tickerClickHandler,
} = appStore

const search = (value?: string): void => {
  if (!value) {
    return
  }

  navigateTo({
    name: 'search',
    query: {
      s: value,
    },
  })
}
</script>

<template>
  <header
    :class="{
      'header--mobile-menu-opened': isMainMenuOpened,
      'header--team-page': team,
    }"
    class="header"
  >
    <Wrapper class="header__wrapper">
      <div class="header__content">
        <div class="header__logo">
          <NuxtLink
            :to="{ name: 'index' }"
            @click="reloadIndexPage"
          >
            <span class="sr-only">The UFL Logo Image</span>
            <UIImage
              v-if="websiteMainImage"
              :image="websiteMainImage"
              class="header__logo-image"
            />
          </NuxtLink>
        </div>
        <UITeamsDropdown
          v-if="selectedTeam"
          v-model="selectedTeam"
          slug-as-value
          is-header
          class="header__dropdown"
        />
        <nav
          class="header__nav"
          :class="{ opened: isMainMenuOpened }"
        >
          <HeaderMenu
            v-if="headerMenu"
            :menu="headerMenu"
            class="header__nav-list"
          />
        </nav>
      </div>
      <ContentBlocks
        v-if="hasSlotContentBlocks"
        :content-blocks="slotContentBlocks"
        class="header__buttons"
      />
      <div class="header__mobile-buttons">
        <UIButton
          variant="clear"
          :appended-icon="isTickerOpened ? 'icon-park-outline:close' : 'lucide:calendar'"
          is-label-hidden
          class="header__mobile-button"
          @click="tickerClickHandler"
        >
          Open Schedule Ticker
        </UIButton>
      </div>
      <HeaderSearch
        class="header__search"
        :is-opened="isSearchOpened"
        @click="searchClickHandler"
        @search="search"
      />
      <HeaderBurger
        v-if="!team"
        :class="{ active: isMainMenuOpened }"
        class="header__mobile-menu-button"
        @click.prevent="mainMenuClickHandler"
      />
    </Wrapper>
  </header>
</template>

<style lang="scss" scoped>
.header {
  &__dropdown {
    @apply ml-auto;
    @apply md:ml-0;

    :deep(.dropdown__select) {
      @apply max-sm:h-[40px] max-sm:text-[14px];
    }
  }
}
</style>

<style lang="scss">
.header {
  @apply h-[70px] sticky left-0 top-0 w-full z-[10002] flex items-center bg-primary border-b border-b-white/20 transition-colors duration-300;
  @apply xl:h-[100px];

  &-overlay {
    @apply h-[70px] relative;
    @apply xl:h-[100px];

    &:has(.header--light) {
      @apply h-auto;
    }
  }

  &__wrapper {
    @apply w-full h-full flex items-center justify-between px-[15px];
    @apply md:px-[20px];
  }

  &__content {
    @apply w-full h-full flex items-center justify-between;
  }

  &__logo {
    @apply mr-[5px];
    @apply md:mr-[40px];
  }

  &__logo-image {
    @apply w-auto h-[30px] block object-contain;
    @apply md:h-[44px];
  }

  &__nav {
    @apply flex flex-col h-full fixed z-[2] top-[70px] pb-[100px] -right-full bg-primary w-full transition-all duration-300;
    @apply md:w-[400px] md:pb-0;
    @apply xl:static xl:w-auto xl:bg-transparent xl:flex-row xl:items-center xl:mx-auto;

    &.opened {
      @apply right-0;
    }

    &-list {
      @apply flex flex-col items-start px-[15px] gap-[20px] h-full py-[20px] overflow-auto;
      @apply xl:flex-row xl:gap-x-[60px] xl:p-0 xl:overflow-visible xl:items-center xl:gap-[30px];
    }

    .team-menu-item {
      @apply inline-flex items-center my-[5px];

      &__image-wrapper {
        @apply mr-[20px] w-[50px] h-[50px] rounded-full flex items-center justify-center border border-white/20;
      }

      &__image {
        @apply w-[26px] h-[26px] object-contain;
      }
    }
  }

  &__nav-list > .menu-list {
    @apply flex flex-col items-start px-[15px] gap-[20px] h-full w-full py-[20px] relative;
    @apply xl:flex-row xl:gap-x-[60px] xl:p-0 xl:overflow-visible xl:items-center xl:gap-[30px] xl:w-auto xl:static;
  }

  &__buttons {
    @apply items-center shrink-0;
  }

  &__mobile-buttons {
    @apply mr-[10px] ml-auto flex items-center gap-[10px];
    @apply xl:hidden;
  }

  &__mobile-button {
    @apply text-white text-[22px] w-[40px] h-[40px] rounded-full bg-white/15 flex items-center justify-center transition-colors duration-300;
    @apply hover:text-white hover:bg-white/15;

    .button__icon {
      @apply text-white w-[20px] h-[20px];
    }

    &:hover .button__icon {
      @apply text-white;
    }
  }

  &__search {
    @apply relative mr-[10px];
    @apply xl:mr-0 xl:ml-[30px];
  }

  &--team-page {
    @apply static h-[70px];
  }

  &--team-page &__wrapper,
  &--team-page &__content {
    @apply justify-start;
  }

  &--team-page &__nav,
  &--team-page &__buttons,
  &--team-page &__mobile-menu-button,
  &--team-page &__mobile-buttons {
    @apply hidden;
  }

  &--team-page &__search {
    @apply mr-0 ml-[10px];
  }
}
</style>
